import { useEffect, useState } from 'react'

/* i18n Dependencies */
import { useIntl } from 'react-intl';
import useMediaQuery from '@material-ui/core/useMediaQuery';

/* API */
import { postProfile, postChat } from 'lib/API';

/* Redux */
import { useSelector } from 'react-redux';

/* Analytics */
import { trackEvent } from 'lib/analytics';

/* UI Components */
import {
  Widget,
  addResponseMessage,
  setQuickButtons,
  addLinkSnippet,
  addUserMessage,
  toggleWidget,
  toggleMsgLoader
} from 'react-chat-widget';

/* Styles */
import 'react-chat-widget/lib/styles.css';

export default function ChatWrapper({ preferredName }) {
  const [openChat, setOpenChat] = useState(false);
  const [pristineChat, setPristineChat] = useState(true);
  const [quickButtonsActive, setQuickButtonsActive] = useState(false);
  const intl = useIntl();
  const matches = useMediaQuery('(max-width:600px)');

  const chatFeedback = useSelector((state) => state.app.chatFeedback);

  const quickButtons = {
    'career' : {
      'thumbsdown' : [
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike1', description: "I'm not interested."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike1', description: "I'm not interested."})
        },
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike2', description: "I'm not qualified."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike2', description: "I'm not qualified."})
        },
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike3', description: "I'm overqualified."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike3', description: "I'm overqualified."})
        },
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike4', description: "Pay is too little."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike4', description: "Pay is too little."})
        },
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike5', description: "Too hard to make the transition."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike5', description: "Too hard to make the transition."})
        },
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike6', description: "Too similar to what I've done before."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike6', description: "Too similar to what I've done before."})
        },
        {
          label: intl.formatMessage({id: 'feedbackCareerDislike7', description: "Something else."}),
          value: intl.formatMessage({id: 'feedbackCareerDislike7', description: "Something else."})
        }
      ]
    },
    'training' : {
      'thumbsdown' : [
        {
          label: intl.formatMessage({id: 'feedbackTrainingDislike1', description: "Training is not relevant."}),
          value: intl.formatMessage({id: 'feedbackTrainingDislike1', description: "Training is not relevant."})
        },
        {
          label: intl.formatMessage({id: 'feedbackTrainingDislike2', description: "I already have these skills."}),
          value: intl.formatMessage({id: 'feedbackTrainingDislike2', description: "I already have these skills."})
        },
        {
          label: intl.formatMessage({id: 'feedbackTrainingDislike3', description: "Too expensive."}),
          value: intl.formatMessage({id: 'feedbackTrainingDislike3', description: "Too expensive."})
        },
        {
          label: intl.formatMessage({id: 'feedbackTrainingDislike4', description: "Something else."}),
          value: intl.formatMessage({id: 'feedbackTrainingDislike4', description: "Something else."})
        }
      ]
    },
    'job' : {
      'thumbsdown' : [
        {
          label: intl.formatMessage({id: 'feedbackJobDislike1', description: "Not interested in employer."}),
          value: intl.formatMessage({id: 'feedbackJobDislike1', description: "Not interested in employer."})
        },
        {
          label: intl.formatMessage({id: 'feedbackJobDislike2', description: "Not interested in this industry."}),
          value: intl.formatMessage({id: 'feedbackJobDislike2', description: "Not interested in this industry."})
        },
        {
          label: intl.formatMessage({id: 'feedbackJobDislike3', description: "The location."}),
          value: intl.formatMessage({id: 'feedbackJobDislike3', description: "The location."})
        },
        {
          label: intl.formatMessage({id: 'feedbackJobDislike4', description: "Pay is too little."}),
          value: intl.formatMessage({id: 'feedbackJobDislike4', description: "Pay is too little."})
        },
        {
          label: intl.formatMessage({id: 'feedbackJobDislike5', description: "I'm not qualified."}),
          value: intl.formatMessage({id: 'feedbackJobDislike5', description: "I'm not qualified."})
        },
        {
          label: intl.formatMessage({id: 'feedbackJobDislike6', description: "I'm overqualified."}),
          value: intl.formatMessage({id: 'feedbackJobDislike6', description: "I'm overqualified."})
        },
        {
          label: intl.formatMessage({id: 'feedbackJobDislike7', description: "Something else."}),
          value: intl.formatMessage({id: 'feedbackJobDislike7', description: "Something else."})
        }
      ]
    }
  };

  useEffect(() => {
    if (openChat) {
      setPristineChat(false);
    }
  }, [openChat]);

  useEffect(() => {
    const { action, item } = chatFeedback;

    if (action === undefined || !action) {
        return;
    }

    const feedback = action === "LIKE_REC"
      ? intl.formatMessage({id: 'skipper.feedback.like'})
      : intl.formatMessage({id: 'skipper.feedback.dislike'});

    if (action === "LIKE_REC") {
      if (quickButtonsActive) {
        setQuickButtons([]);
        setQuickButtonsActive(false);
      }

      addResponseMessage(intl.formatMessage(
        {
          defaultMessage: "Thanks for letting me know that you {feedback} the {section} recommendation for {title}. I will use that feedback for future recommendations.",
          id: 'feedbackLike',
        },
        {
          feedback: feedback,
          section: intl.formatMessage({id: `skipper.section.${item.type}`}),
          title: item.item.title,
        }
      ));

      if (item.type === "training") {
        addLinkSnippet({
          title: intl.formatMessage({id: 'feedbackTrainingLike', defaultMessage: "Learn more!"}),
          link: item.item.link,
          target: '_blank'
        });
      } else if (item.type === "job") {
        addLinkSnippet({
          title: intl.formatMessage({id: 'feedbackJobLike', defaultMessage: "Learn more!"}),
          link: item.item.learn,
          target: '_blank'
        });
      }
    } else {
      addResponseMessage(intl.formatMessage(
        {
          defaultMessage: "Thanks for letting me know that you {feedback} the {section} recommendation for {title}. I will use that feedback for future recommendations. Could you tell me more about why you {feedback} it?",
          id: 'feedbackDislike',
        },
        {
          feedback: feedback,
          section: intl.formatMessage({id: `skipper.section.${item.type}`}),
          title: item.item.title,
        }
      ));
      setQuickButtons(quickButtons[item.type].thumbsdown);
      setQuickButtonsActive(true);
    }

    if (!openChat) {
      setOpenChat(!openChat);
      toggleWidget();
    }
  }, [chatFeedback]);

  useEffect(() => {
    if (preferredName) {
      addResponseMessage(intl.formatMessage(
        {id: 'skipper.intro', description: 'string'},
        { preferredName }
      ));
    } else {
      addResponseMessage(intl.formatMessage(
        {id: 'skipper.introNoName', description: 'string'}
      ));
    }
  }, []);

  const handleSend = (userMessage) => {
    toggleMsgLoader();
    postChat({
      session: 'Career_Compass_V1',
      message: userMessage,
      language: intl.locale,
    }).then(response => {
      toggleMsgLoader();
      addResponseMessage(response.message);
    }).catch(error => {
      toggleMsgLoader();
      console.log(error);
    })
  }

  const quickButtonClicked = (userMessage) => {
    const { action, item } = chatFeedback;
    if (quickButtonsActive) {
      setQuickButtons([]);
      setQuickButtonsActive(false);
    }
    addUserMessage(userMessage);
    toggleMsgLoader();
    postProfile({
      action: 'event',
      name: 'CHAT_FEEDBACK',
      item: { ...item, 'message': userMessage }
    }).then(response => {
      toggleMsgLoader();
      addResponseMessage(intl.formatMessage(
        {
          defaultMessage: "Thanks for your feedback! I learn and get better all the time, and soon I'll be able to work with you to refine your recommendations.",
          id: 'feedbackDislikeResponse',
        }
      ))
    }).catch(error => {
      toggleMsgLoader();
      console.log(error);
    })
  }

  const toggleIcon = (handleToggle) => {
    trackEvent('CHAT_TOGGLE', {current_state: openChat});
    setOpenChat(!openChat)
    handleToggle();
  }

  const getCustomLauncher = (handleToggle, toggleIcon) => {
    return !openChat ? (
      pristineChat ? (
        <button
          type="button"
          onClick={() => toggleIcon(handleToggle)}
          className={`rcw-launcher${openChat ? ' rcw-hide-sm' : ''}`}
        />
      ) : (
          <button
            type="button"
            onClick={() => toggleIcon(handleToggle)}
            className={`rcw-launcher rcw-launcher--mini`}
          />
      )            
    ) : (
      <button
        className="minimize-chat-button"
        style={{pointerEvents: 'all'}}
        type="button"
        onClick={() => toggleIcon(handleToggle)}
      />        
    );
  }

  return (
    <Widget
      title={intl.formatMessage({id: 'skipper.title', description: 'string'})}
      handleNewUserMessage={handleSend}
      handleQuickButtonClicked={quickButtonClicked}
      senderPlaceHolder={intl.formatMessage({id: 'skipper.askSomething', description: 'string'})}
      launcher={handleToggle => getCustomLauncher(handleToggle, toggleIcon)}
      subtitle={''}
      showCloseButton={false}
      fullScreenMode={false}
      titleAvatar={'/images/anchor-title.svg'}
      profileAvatar={'/images/anchor-profile.svg'}
    />
  )
}
